export type Product = "construction";

/** DB-stable discriminator stamped on new Atriam workspaces. */
export type SignupProduct = "atriam";

export type ModuleId = "construction-suite";
export type ShellId = "construction";
export type ViewId = "construction";
export type OnboardingFlowId = "construction";
export type PostWorkspaceStep = "personalize";

export interface OnboardingConfig {
  flow: OnboardingFlowId;
  businessClaiming: true;
  workerPersona: true;
  postWorkspaceStep: PostWorkspaceStep;
  /** Plan state stamped when this product is activated on a workspace. */
  activation: {
    planTier: string;
    subscriptionStatus: "active";
  };
}

export interface ProductDef {
  id: Product;
  hostnames: readonly string[];
  signupProduct: SignupProduct;
  modules: readonly ModuleId[];
  cronOwner: true;
  forcedTheme: null;
  shell: ShellId;
  views: {
    dashboard: ViewId;
    landing: ViewId;
    login: ViewId;
    legalBackdrop: "columns";
  };
  onboarding: OnboardingConfig;
  billing: {
    catalog: "construction";
    requiresNumberSubscription: false;
    outreachRequiresPaidPlan: false;
  };
  publicRoutes: {
    community: true;
  };
  branding: {
    appName: string;
    wordmark: string;
    themeColor: string;
    iconBase: string;
    description: string;
    ogAlt: string;
    marketingTitle: string;
    marketingDescription: string;
    mark: "column";
    pwaDescription: string;
    pwaShortcuts: readonly {
      name: string;
      short_name: string;
      url: string;
      description: string;
    }[];
  };
  releaseModules: readonly string[];
}

export const PRODUCTS: Record<Product, ProductDef> = {
  construction: {
    id: "construction",
    hostnames: ["atriam.ai", "www.atriam.ai"],
    signupProduct: "atriam",
    modules: ["construction-suite"],
    cronOwner: true,
    forcedTheme: null,
    shell: "construction",
    views: {
      dashboard: "construction",
      landing: "construction",
      login: "construction",
      legalBackdrop: "columns",
    },
    onboarding: {
      flow: "construction",
      businessClaiming: true,
      workerPersona: true,
      postWorkspaceStep: "personalize",
      activation: {
        planTier: "free",
        subscriptionStatus: "active",
      },
    },
    billing: {
      catalog: "construction",
      requiresNumberSubscription: false,
      outreachRequiresPaidPlan: false,
    },
    publicRoutes: {
      community: true,
    },
    branding: {
      appName: "Atriam",
      wordmark: "Atriam",
      themeColor: "#c2410c",
      iconBase: "/icons/construction",
      description:
        "Run your construction business end to end — leads, projects, clients, and your team in one place.",
      ogAlt: "Atriam — construction business management",
      marketingTitle: "Atriam — construction business management",
      marketingDescription:
        "Run your construction business end to end — leads, projects, clients, and your team in one place.",
      mark: "column",
      pwaDescription: "Atriam is your construction business swiss knife",
      pwaShortcuts: [
        {
          name: "Open Contractors",
          short_name: "Contractors",
          // "/" 302s to /dashboard — the contractors directory lives at
          // /contractors (lib/navigation/destinations.ts).
          url: "/contractors",
          description: "Jump straight into contractor search",
        },
        {
          name: "Projects",
          short_name: "Projects",
          url: "/projects",
          description: "Review saved project teams",
        },
      ],
    },
    // Kept to what production actually opens. PostHog pageviews for the 30
    // days to 2026-07-24: contractors 46, hiring 11, compliance 2,
    // professionals 2, dir-vendors 1 — and documents / payroll / timesheet /
    // public-works exactly 0 across every workspace.
    //
    // The remaining parked keys (documents, public-works) are parked rather than
    // deleted: this list is only the "on for everyone" default, so a workspace
    // with explicit `module_access` or a tier entitlement still reaches them,
    // their API routes are untouched (guardModules gates pages only), and
    // re-launching one is adding its key back. Re-check the pageview counts
    // before parking or launching any module.
    //
    // payroll launched 2026-07-29 with the CA W-2 employment compliance layer
    // (hire-pack obligations, sick-leave accrual, LC 226 wage statements,
    // employment tax filings). Its zero pageviews reflected a module that could
    // compute a paycheck but could not discharge any of the duties attached to
    // having employees; that gap is what closed. The separate `timesheet`
    // module was folded into /payroll?tab=timesheets on 2026-08-03.
    releaseModules: [
      // Money left the always-on core 2026-10-21 so it can be withheld from
      // members; it stays on for everyone the member gate lets through.
      "finances",
      "contractors",
      "dir-vendors",
      "compliance",
      "professionals",
      "hiring",
      "payroll",
      "documents",
    ],
  },
};

export const DEFAULT_PRODUCT: Product = "construction";
export const PRODUCT: Product = "construction";
export const PRODUCT_DEF: ProductDef = PRODUCTS.construction;

/** Atriam is a single-product deployment; the arguments remain for callers. */
export function deriveProduct(_siteUrl?: string, _override?: string): Product {
  return PRODUCT;
}

export function productDefForSignupProduct(
  _signupProduct: SignupProduct,
): ProductDef {
  return PRODUCT_DEF;
}

export function onboardingForSignupProduct(
  signupProduct: SignupProduct,
): OnboardingConfig {
  return productDefForSignupProduct(signupProduct).onboarding;
}

export function productHasModule(
  module: ModuleId,
  _product: Product = PRODUCT,
): boolean {
  return PRODUCT_DEF.modules.includes(module);
}

export const IS_CRON_OWNER = true;
